import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setBonusPrizeData, addBonusSchedule, removeBonusSchedule } from '../store/bonusPrizeSlice';
import GravityLogo from './GravityLogo';
import { setProductData } from '../store/productSlice';
import { setAdminData } from '../store/adminSlice';
import { PW_BonusPrize_API, PW_BonusPrize_Save_API, PW_BonusPrize_Delete_API, PW_Products_API, PW_Stores_API } from '../Server/ApiV2';
import Spinner from './Spinner';
import { isPick6 } from '../utils/pick6';

export default function BonusPrizes() {
  const dispatch = useDispatch();
  const { bonusSchedules } = useSelector(s => s.bonusPrize);
  const { products } = useSelector(s => s.product);
  const { stores } = useSelector(s => s.admin);
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [bonusRes, prodRes, storeRes] = await Promise.all([
        PW_BonusPrize_API(), PW_Products_API(), PW_Stores_API(),
      ]);
      if (cancelled) return;
      if (bonusRes?.data?.success) dispatch(setBonusPrizeData(bonusRes.data.data));
      if (prodRes?.data?.success) dispatch(setProductData(prodRes.data.data));
      if (storeRes?.data?.success) dispatch(setAdminData({ stores: storeRes.data.data }));
      setPageLoading(false);
    })();
    return () => { cancelled = true; };
  }, [dispatch]);

  const activeStores = stores.filter(s => s.status === 'Active');
  const activeProducts = products.filter(p => p.status === 'Active');

  const [editing, setEditing] = useState(null);
  const [storeIds, setStoreIds] = useState([]);
  const [scheduleDate, setScheduleDate] = useState('');
  const [productId, setProductId] = useState('');
  const [startTime, setStartTime] = useState('00:00');
  const [endTime, setEndTime] = useState('23:59');
  const [denomination, setDenomination] = useState('');
  const [formError, setFormError] = useState('');
  const [askDelete, setAskDelete] = useState(null); // id of the bonus whose delete is being confirmed
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null); // { id, text }

  const ALL_DENOMINATIONS = [5, 10, 20, 30, 50, 70, 100, 200, 500, 1000, 5000];

  const selectedProduct = products.find(p => p.id === productId);
  const factor = selectedProduct?.priceAmount || 1;
  // Elite's bonus prize prints as one of its winning numbers, so only its
  // option prizes can be chosen.
  const pick6 = isPick6(selectedProduct);
  const denomChoices = pick6
    ? [...new Set((selectedProduct.winningOptions || []).map(o => Number(o.prize)))].sort((a, b) => a - b)
    : ALL_DENOMINATIONS;

  const handleSelectStore = (sid) => {
    setStoreIds([sid]);
  };

  const resetForm = () => {
    setEditing(null); setStoreIds([]); setScheduleDate(''); setProductId('');
    setStartTime('00:00'); setEndTime('23:59');
    setDenomination(''); setFormError('');
  };

  const handleSave = () => {
    if (!storeIds.length) { setFormError('Select at least one store'); return; }
    if (!scheduleDate) { setFormError('Select a date'); return; }
    if (!productId) { setFormError('Select a product'); return; }
    if (startTime >= endTime) { setFormError('Start Time must be before End Time'); return; }
    if (!denomination) { setFormError('Select a denomination'); return; }

    const existing = editing ? bonusSchedules.find(bs => bs.id === editing) : null;

    setFormError('');
    const bonusId = existing?.id || `BPS-${Date.now()}`;

    const bonusData = {
      id: bonusId, scheduleDate, productId, storeIds,
      startTime, endTime, status: 'Active',
      bonusWinnerCount: 1,
      denominations: { [denomination]: 0 },
      createdAt: existing?.createdAt || new Date().toISOString(),
    };
    dispatch(addBonusSchedule(bonusData));
    PW_BonusPrize_Save_API(bonusData).catch(err => console.error('[Bonus] Save failed:', err));
    resetForm();
  };

  const handleEdit = (bs) => {
    setEditing(bs.id);
    setStoreIds(bs.storeIds || []);
    setScheduleDate(bs.scheduleDate ? bs.scheduleDate.split('T')[0] : '');
    setProductId(bs.productId);
    setStartTime(bs.startTime || '00:00');
    setEndTime(bs.endTime || '23:59');
    const denomKeys = Object.keys(bs.denominations || {});
    setDenomination(denomKeys.length ? denomKeys[0] : '');
    setFormError('');
  };

  // Deletes only after the server confirms; a failure leaves the bonus in place.
  const handleDelete = async (bs) => {
    if (deleting) return;
    setDeleting(true);
    setDeleteError(null);
    const res = await PW_BonusPrize_Delete_API(bs.id);
    setDeleting(false);
    setAskDelete(null);
    if (!res?.data?.success) {
      const why = res?.data?.message || res?.response?.data?.message || res?.message || '';
      setDeleteError({ id: bs.id, text: `Could not delete the bonus. Nothing was changed. ${why}`.trim() });
      return;
    }
    if (editing === bs.id) resetForm();
    dispatch(removeBonusSchedule(bs.id));
  };

  const formatDate = (d) => {
    if (!d) return '—';
    const dt = new Date(typeof d === 'string' && d.includes('T') ? d : d + 'T00:00');
    return dt.toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });
  };

  if (pageLoading) return <div className="page-loading"><Spinner /></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Bonus Prizes</h2>
          <div className="page-header-sub">Create additional prize winners during scheduled periods</div>
        </div>
      </div>

      <div className="card mb-24">
        <div className="card-title gold">{editing ? 'Edit Schedule' : 'New Additional Prize Winner'}</div>
        <div className="card-body">

          <div className="cal-section">
            <div className="cal-label">Stores</div>
            <div className="cal-store-grid">
              {activeStores.map(store => (
                <label key={store.id} className={`cal-store-check${storeIds.includes(store.id) ? ' selected' : ''}`}>
                  <input type="radio" name="bonus-store"
                    checked={storeIds.includes(store.id)}
                    onChange={() => handleSelectStore(store.id)}
                    disabled={!!editing} />
                  <span>{store.name}</span>
                  <span className="text-sm text-muted" style={{ marginLeft: 'auto' }}>{store.code}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="cal-section">
            <div className="cal-label">Date</div>
            <input type="date" className="form-input" value={scheduleDate}
              onChange={e => setScheduleDate(e.target.value)}
              style={{ maxWidth: 200 }} disabled={!!editing} />
          </div>

          <div className="cal-section">
            <div className="cal-label">Product</div>
            <div className="cal-store-grid">
              {activeProducts.map(p => (
                <label key={p.id} className={`cal-store-check${productId === p.id ? ' selected' : ''}`}>
                  <input type="radio" name="bonus-product"
                    checked={productId === p.id}
                    onChange={() => { setProductId(p.id); setDenomination(''); }}
                    disabled={!!editing} />
                  <span>{p.name}</span>
                  <span className="text-sm text-muted" style={{ marginLeft: 'auto' }}>Factor {p.priceAmount || 1}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="cal-section">
            <div className="cal-label">Time Window</div>
            <div className="form-grid">
              <div>
                <div className="text-sm text-muted mb-8">Start Time</div>
                <input type="time" className="form-input" value={startTime}
                  onChange={e => setStartTime(e.target.value)} />
              </div>
              <div>
                <div className="text-sm text-muted mb-8">End Time</div>
                <input type="time" className="form-input" value={endTime}
                  onChange={e => setEndTime(e.target.value)} />
              </div>
            </div>
            <div className="text-sm text-muted mt-8">
              Active during {startTime} – {endTime}
            </div>
          </div>

          {productId && (
            <div className="cal-section">
              <div className="cal-label">Denomination</div>
              <div className="text-sm text-muted mb-8">
                {pick6
                  ? `The first ${selectedProduct.name} coupon sold in the window gets this prize on one more of the customer's numbers, on top of any prize it already wins. Only ${selectedProduct.name}'s winning-option prizes can be chosen.`
                  : '1 additional winner of this denomination will be added per batch'}
              </div>
              <div className="cal-store-grid">
                {denomChoices.map(d => (
                  <label key={d} className={`cal-store-check${Number(denomination) === d ? ' selected' : ''}`}
                    style={{ minWidth: 80, justifyContent: 'center' }}>
                    <input type="radio" name="bonus-denom"
                      checked={Number(denomination) === d}
                      onChange={() => setDenomination(String(d))} />
                    <span className="fw-600">{d.toLocaleString()}</span>
                    <span className="text-sm text-muted" style={{ marginLeft: 4 }}>
                      = {(d * factor).toLocaleString()}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {formError && (
            <div style={{ color: '#c62828', background: '#fef2f2', padding: '8px 12px', borderRadius: 6, fontSize: 13, marginTop: 8 }}>
              {formError}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button className="btn btn-success" onClick={handleSave}
              disabled={!storeIds.length || !scheduleDate || !productId || !denomination}>
              {editing ? 'Update' : 'Save'}
            </button>
            {editing && <button className="btn btn-secondary" onClick={resetForm}>Cancel</button>}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-title">Bonus Schedules ({bonusSchedules.length})</div>
        <div className="card-body">
          {bonusSchedules.length === 0 ? (
            <div className="empty-state"><GravityLogo size={48} circle /><p>No bonus schedules configured yet</p></div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {[...bonusSchedules].sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || '')).map(bs => {
                const prod = products.find(p => p.id === bs.productId);
                const f = prod?.priceAmount || 1;
                const denom = Number(Object.keys(bs.denominations || {})[0]) || 0;
                const prize = denom * f;
                const bsDate = bs.scheduleDate ? bs.scheduleDate.split('T')[0] : '';
                const nowD = new Date();
                const todayISO = nowD.toISOString().split('T')[0];
                const nowHHMM = `${String(nowD.getHours()).padStart(2, '0')}:${String(nowD.getMinutes()).padStart(2, '0')}`;
                const expired = bsDate < todayISO;
                // Live window state — whether the bonus can actually fire right now
                // (distinct from Status: a schedule can be "Active" yet outside its time window today)
                let windowState = null; // 'live' | 'closed' | 'upcoming'
                if (bs.status === 'Active' && !expired) {
                  if (bsDate > todayISO || nowHHMM < (bs.startTime || '00:00')) windowState = 'upcoming';
                  else if (nowHHMM > (bs.endTime || '23:59')) windowState = 'closed';
                  else windowState = 'live';
                }
                return (
                  <div key={bs.id} className="cal-schedule-card">
                    {/* An expired bonus is shown faded; its Delete stays at full strength. */}
                    <div style={expired ? { opacity: 0.6 } : undefined}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <div>
                          <span className="fw-700" style={{ fontSize: 15, color: '#2d1b69' }}>
                            {prod?.name || bs.productId}
                          </span>
                          <span className="chip chip-gold" style={{ marginLeft: 8 }}>+1 Winner</span>
                          <span className="chip chip-purple" style={{ marginLeft: 4, fontSize: 10 }}>
                            {bs.startTime} – {bs.endTime}
                          </span>
                        </div>
                        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                          {windowState === 'live' && (
                            <span className="chip chip-green" style={{ fontSize: 10 }}>● Live now</span>
                          )}
                          {windowState === 'closed' && (
                            <span className="chip" style={{ fontSize: 10, background: '#fde8e8', color: '#c0392b' }}>Window closed today</span>
                          )}
                          {windowState === 'upcoming' && (
                            <span className="chip" style={{ fontSize: 10, background: '#fff8e6', color: '#c9a227' }}>Upcoming</span>
                          )}
                          {bs.executedCount > 0 && (
                            <span className="chip chip-green" style={{ fontSize: 10 }}>
                              Executed ({bs.executedCount})
                            </span>
                          )}
                          {!expired && bs.executedCount === 0 && (
                            <span className="chip" style={{ fontSize: 10, background: '#f0f0f0', color: '#888' }}>
                              Not Executed
                            </span>
                          )}
                          <span className={`chip ${expired ? 'chip-red' : bs.status === 'Active' ? 'chip-green' : 'chip-red'}`}>
                            {expired ? 'Expired' : bs.status}
                          </span>
                        </div>
                      </div>
                      <div className="mb-8">
                        <span className="text-sm text-muted">Date: </span>
                        <span className="text-sm fw-600">{formatDate(bs.scheduleDate)}</span>
                      </div>
                      <div className="mb-8">
                        <span className="text-sm text-muted">Stores: </span>
                        <span className="text-sm fw-600">
                          {(bs.storeIds || []).map(sid => stores.find(st => st.id === sid)?.name || sid).join(', ') || '—'}
                        </span>
                      </div>
                      <div className="mb-8">
                        <span className="text-sm text-muted">Denomination: </span>
                        <span className="text-sm fw-700" style={{ color: '#c9a227' }}>{denom.toLocaleString()}</span>
                        <span className="text-sm text-muted"> → Prize: </span>
                        <span className="text-sm fw-700" style={{ color: '#c9a227' }}>{prize.toLocaleString()}</span>
                      </div>
                    </div>
                    {/* Every bonus can be deleted, expired ones too; only upcoming or current ones can be edited. */}
                    {askDelete === bs.id ? (
                      <div className="schedcal-callout danger" role="alert" style={{ marginTop: 4 }}>
                        <div className="fw-600">Delete this bonus?</div>
                        <div className="text-sm">
                          {prod?.name || bs.productId} · {formatDate(bs.scheduleDate)} · {bs.startTime} – {bs.endTime}.
                          {bs.executedCount > 0
                            ? ` It has paid ${bs.executedCount} prize${bs.executedCount === 1 ? '' : 's'}; those sales keep their prize.`
                            : ''}
                        </div>
                        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                          <button className="btn btn-secondary btn-sm" onClick={() => setAskDelete(null)} disabled={deleting}>Keep Bonus</button>
                          <button className="btn btn-danger btn-sm" onClick={() => handleDelete(bs)} disabled={deleting}>
                            {deleting ? 'Deleting…' : 'Yes, Delete'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                        {!expired && <button className="btn btn-outline btn-sm" onClick={() => handleEdit(bs)}>Edit</button>}
                        <button className="btn btn-sm cal-remove-btn" onClick={() => { setDeleteError(null); setAskDelete(bs.id); }}>Delete</button>
                      </div>
                    )}
                    {deleteError?.id === bs.id && (
                      <div style={{ color: '#c62828', background: '#fef2f2', padding: '8px 12px', borderRadius: 6, fontSize: 13, marginTop: 8 }}>
                        {deleteError.text}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
